import type {
  Contact,
  Episode,
  Favorite,
  FamilyMessage,
  ListResponse,
  MediaSeries,
  PlaybackContinue,
  RadioStation,
  SessionMe,
  StreamGrant,
  TodayInfo,
} from "./types";

let csrfToken = "";

export const SESSION_EXPIRED_EVENT = "voice-companion.session-expired";
export const NETWORK_FAILURE_MESSAGE = "网络暂时不可用，请恢复网络后再试。";

type RequestOptions = {
  method?: string;
  body?: unknown;
  csrf?: boolean;
};

const memoryRequestCache = new Map<string, Promise<unknown>>();

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error instanceof Error &&
      [
        "Failed to fetch",
        "Load failed",
        "NetworkError",
        NETWORK_FAILURE_MESSAGE,
      ].some((text) => error.message.includes(text)))
  );
}

export function createActionIdempotencyKey(prefix: string): string {
  const randomPart =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${randomPart}`;
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (options.csrf) {
    headers.set("X-CSRF-Token", csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? "GET",
      credentials: "same-origin",
      headers,
      body:
        options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch (error) {
    if (isNetworkError(error)) {
      throw new Error(NETWORK_FAILURE_MESSAGE);
    }
    throw error;
  }

  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as {
      detail?: { code?: string; message?: string };
    } | null;
    const message = detail?.detail?.message ?? `请求失败：${response.status}`;
    if (
      response.status === 401 &&
      detail?.detail?.code === "device_not_activated"
    ) {
      csrfToken = "";
      window.dispatchEvent(
        new CustomEvent(SESSION_EXPIRED_EVENT, {
          detail: { message: "激活已经失效，请重新输入激活码。" },
        }),
      );
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

function cachedRequest<T>(path: string, refresh = false): Promise<T> {
  if (refresh) {
    memoryRequestCache.delete(path);
  }
  const cached = memoryRequestCache.get(path);
  if (cached) {
    return cached as Promise<T>;
  }
  const promise = request<T>(path).catch((error) => {
    memoryRequestCache.delete(path);
    throw error;
  });
  memoryRequestCache.set(path, promise);
  return promise;
}

function episodesPath(seriesId: string, cursor?: string | null): string {
  return `/api/v1/media-series/${seriesId}/episodes?page_size=20${cursor ? `&cursor=${cursor}` : ""}`;
}

function prefetchCorePageData(): void {
  const mediaSeriesRequest = cachedRequest<ListResponse<MediaSeries>>(
    "/api/v1/media-series?page_size=20",
  );
  const episodeWarmup = mediaSeriesRequest
    .then((result) =>
      Promise.allSettled(
        result.items
          .slice(0, 3)
          .map((series) =>
            cachedRequest<ListResponse<Episode>>(episodesPath(series.id)),
          ),
      ),
    )
    .catch(() => undefined);

  void Promise.allSettled([
    mediaSeriesRequest,
    cachedRequest<ListResponse<RadioStation>>("/api/v1/radio-stations"),
    episodeWarmup,
  ]);
}

export function clearApiRequestCacheForTests(): void {
  memoryRequestCache.clear();
}

export const api = {
  sessionMe: () => request<SessionMe>("/api/v1/session/me"),
  activate: (token: string) =>
    request<{ activated: true; message: string }>("/api/v1/session/activate", {
      method: "POST",
      body: { token },
    }),
  prefetchCorePageData,
  today: () => request<TodayInfo>("/api/v1/today"),
  playbackContinue: () =>
    request<PlaybackContinue>("/api/v1/playback-continue"),
  recentPositions: () =>
    request<ListResponse<PlaybackContinue>>(
      "/api/v1/playback-positions/recent",
    ),
  mediaSeries: (options: { refresh?: boolean } = {}) =>
    cachedRequest<ListResponse<MediaSeries>>(
      "/api/v1/media-series?page_size=20",
      options.refresh,
    ),
  mediaSeriesDetail: (seriesId: string) =>
    cachedRequest<MediaSeries>(`/api/v1/media-series/${seriesId}`),
  episodes: (
    seriesId: string,
    cursor?: string | null,
    options: { refresh?: boolean } = {},
  ) =>
    cachedRequest<ListResponse<Episode>>(
      episodesPath(seriesId, cursor),
      options.refresh,
    ),
  hasCachedEpisodes: (seriesId: string, cursor?: string | null) =>
    memoryRequestCache.has(episodesPath(seriesId, cursor)),
  mediaGrant: (episodeId: string) =>
    request<StreamGrant>(`/api/v1/media-episodes/${episodeId}/stream-grant`, {
      method: "POST",
      csrf: true,
      body: {},
    }),
  messages: () =>
    request<ListResponse<FamilyMessage>>(
      "/api/v1/family-messages?page_size=20",
    ),
  messageGrant: (messageId: string) =>
    request<StreamGrant>(`/api/v1/family-messages/${messageId}/stream-grant`, {
      method: "POST",
      csrf: true,
      body: {},
    }),
  radioStations: (options: { refresh?: boolean } = {}) =>
    cachedRequest<ListResponse<RadioStation>>(
      "/api/v1/radio-stations",
      options.refresh,
    ),
  contacts: () => request<ListResponse<Contact>>("/api/v1/contacts"),
  favorites: () => request<ListResponse<Favorite>>("/api/v1/favorites"),
  peaceReport: (idempotencyKey = createActionIdempotencyKey("peace")) =>
    request<{ id: string; status: string; message: string }>(
      "/api/v1/peace-reports",
      {
        method: "POST",
        csrf: true,
        body: { idempotency_key: idempotencyKey },
      },
    ),
  helpRequest: (idempotencyKey = createActionIdempotencyKey("help")) =>
    request<{ id: string; status: string; message: string }>(
      "/api/v1/help-requests",
      {
        method: "POST",
        csrf: true,
        body: { idempotency_key: idempotencyKey },
      },
    ),
  progress: (payload: Record<string, unknown>) =>
    request<{ accepted: boolean }>("/api/v1/playback-positions/progress", {
      method: "POST",
      csrf: true,
      body: payload,
    }),
  completeProgress: (payload: Record<string, unknown>) =>
    request<{ accepted: boolean; completed: boolean }>(
      "/api/v1/playback-positions/complete",
      {
        method: "POST",
        csrf: true,
        body: payload,
      },
    ),
  uncompleteProgress: (payload: Record<string, unknown>) =>
    request<{ accepted: boolean; completed: boolean }>(
      "/api/v1/playback-positions/uncomplete",
      {
        method: "POST",
        csrf: true,
        body: payload,
      },
    ),
};

export function prefetchFatherNavigationData(): void {
  void Promise.allSettled([
    api
      .mediaSeries()
      .then((result) =>
        Promise.allSettled(
          result.items
            .slice(0, 2)
            .flatMap((series) => [
              api.mediaSeriesDetail(series.id),
              api.episodes(series.id),
            ]),
        ),
      ),
    api.radioStations(),
  ]);
}
